import React, { useEffect, useState, useCallback, useRef, startTransition } from "react";
import {
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress,
  Badge,
  IconButton,
  Alert,
  Snackbar,
  Button,
  Divider,
} from "@mui/material";
import Tree from "rc-tree";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { api } from "../../redux/api";
import LightTooltip from "../../commonComponents/LightTooltip";
import DeployContextMenu from "./DeployContextMenu";
import { treeActions } from "../../redux/slices/treeSlice";
import HelpPanel from "../../components/Help/HelpPanel";

function Deploy({ urlParams }) {
  const [treeData, setTreeData] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [loadingFolderList, setLoadingFolderList] = useState(false);
  const [userSpaceChecked, setUserSpaceChecked] = useState(true);
  const [serverChecked, setServerChecked] = useState(false);
  const [contextMenuType, setContextMenuType] = useState(null); // 'template' or 'folder'
  const [contextMenuNode, setContextMenuNode] = useState(null);
  const [errorAlert, setErrorAlert] = useState({ open: false, message: "" });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Use refs to capture current state values without triggering re-renders
  const expandedKeysRef = useRef([]);
  const selectedKeysRef = useRef([]);
  const selectedNodesRef = useRef([]);

  // Update refs whenever state changes
  useEffect(() => {
    expandedKeysRef.current = expandedKeys;
  }, [expandedKeys]);

  useEffect(() => {
    selectedKeysRef.current = selectedKeys;
  }, [selectedKeys]);

  useEffect(() => {
    selectedNodesRef.current = selectedNodes;
  }, [selectedNodes]);

  const nodeIcon = {
    inboundTemplate: "GET_DATA",
    outboundTemplate: "SEND_DATA",
    workflow: "CALL_WORKFLOW",
    workflow_group: "RECORD",
    DOCUMENT: "TRACKINGREPORT",
    template_folder: "RECORD",
  };

  const iconByLocation = {
    SERVER: "SERVER",
    BOTH: "SERVER_USER_DEPLOY",
    USERSPACE: "USER_SPACE",
  };

  // Function to get all keys from tree data for expand all functionality
  const getAllTreeKeys = (data) => {
    const keys = [];
    const traverse = (nodes) => {
      nodes.forEach((node) => {
        keys.push(node.key);
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      });
    };
    traverse(data);
    return keys;
  };

  /** Keys of non-leaf nodes only — rc-tree does not need leaf keys in expandedKeys; avoids huge arrays */
  const getExpandableTreeKeys = (data) => {
    const keys = [];
    const traverse = (nodes) => {
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          keys.push(node.key);
          traverse(node.children);
        }
      });
    };
    traverse(data);
    return keys;
  };

  // Function to get selected node objects from tree data based on selected keys
  const getSelectedNodesFromKeys = (keys, data) => {
    const selectedNodes = [];
    const traverse = (nodes) => {
      nodes.forEach((node) => {
        if (keys.includes(node.key)) {
          selectedNodes.push(node);
        }
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      });
    };
    traverse(data);
    return selectedNodes;
  };

  // Function to get all visible nodes in tree order (for range selection)
  const getVisibleNodesInOrder = (data, expandedKeys) => {
    const visibleNodes = [];
    const traverse = (nodes) => {
      nodes.forEach((node) => {
        visibleNodes.push(node);
        if (node.children && node.children.length > 0 && expandedKeys.includes(node.key)) {
          traverse(node.children);
        }
      });
    };
    traverse(data);
    return visibleNodes;
  };

  // Function to get range of nodes between two keys
  const getNodeRange = (startKey, endKey, data, expandedKeys) => {
    const visibleNodes = getVisibleNodesInOrder(data, expandedKeys);
    const startIndex = visibleNodes.findIndex(node => node.key === startKey);
    const endIndex = visibleNodes.findIndex(node => node.key === endKey);
    
    if (startIndex === -1 || endIndex === -1) {
      return [];
    }
    
    const minIndex = Math.min(startIndex, endIndex);
    const maxIndex = Math.max(startIndex, endIndex);
    
    return visibleNodes.slice(minIndex, maxIndex + 1).map(node => node.key);
  };

  const EXPAND_ALL_WARN_THRESHOLD = 500;

  const handleExpandAll = () => {
    const expandableKeys = getExpandableTreeKeys(treeData);
    if (expandableKeys.length > EXPAND_ALL_WARN_THRESHOLD) {
      const msg = t("MSG_DEPLOY_EXPAND_ALL_MANY_FOLDERS", { count: expandableKeys.length });
      if (typeof window !== "undefined" && window.confirm && !window.confirm(msg)) {
        return;
      }
    }
    startTransition(() => {
      setExpandedKeys(expandableKeys);
    });
  };

  // Collapse all nodes
  const handleCollapseAll = () => {
    setExpandedKeys([]);
  };

  const Icon = (node) => {
    // Show loader if this node has loading state
    if (node.isLoading) {
      return <CircularProgress size={"16px"} />;
    }

    if (
      (node.type === "INBOUND" ||
        node.type === "OUTBOUND" ||
        node.type === "WORKFLOW" ||
        node.type === "SCHEDULE") && node.isTemplateFile &&
      ["SERVER", "BOTH", "USERSPACE"].includes(node?.status)
    ) {
      if (node?.status === "SERVER") {
        return (
          <>
            <span className={node?.isLocked ? "LOCK" : iconByLocation[node?.status]}></span>
          </>
        );
      } else if (node?.status === "BOTH") {
        return (
          <>
            <span className={node?.isLocked ? "USER_SPACE" : iconByLocation[node?.status]}></span>
            {node?.isLocked && <span className="LOCK"></span>}
          </>
        );
      } else {
        return (
            <span className={iconByLocation[node?.status]}></span>
        );
      }
    } else {
      return (
        <>
          <span className={node?.isDocument ? "DOCUMENT" : nodeIcon[node.type]}></span>
          {node?.isLocked && <span className="LOCK"></span>}
        </>
      );
    }
  };

  const switcherIcon = (obj) => {
    if (obj.isLeaf) return;
    if (obj.expanded) return <ArrowDropDownIcon />;
    else return <ArrowRightIcon className="deploy-arrow-icon" />;
  };

  // Helper function to determine icon margin for proper alignment
  const getIconMargin = (node) => {
    // Apply margin when node has compound icons or locked document icons
    if (node?.status === "BOTH" || (node?.isDocument && node?.isLocked)) {
      return '12px';
    }
    return '0px';
  };

  const titleRender = (node) => {
    return (
      <div className="deploy-tree-node-container">
        {/* Node title with badge */}
        <div className="deploy-tree-file-column">
          <div className="deploy-tree-file-content">
            <span 
              className="deploy-tree-file-name" 
              data-key={node.key}
              style={{ 
                marginLeft: getIconMargin(node)
              }}
             >
              {node?.lockStatus && node?.lockStatus !== "null" ? (
                <>
                  {node.title}
                  &nbsp;
                  [<i style={{ margin: "2px" }}>{node.lockStatus}</i>]
                </>
              ) : (
                node.title
              )}
            </span>
            {node?.fileCount > 0 && (
              <Badge
                badgeContent={node.fileCount}
                color="primary"
                className="deploy-tree-badge">
                <div className="deploy-badge-empty-box" />
              </Badge>
            )}
          </div>
        </div>

        {/* Data columns */}
        <div className="deploy-tree-data-columns">
          {userSpaceChecked && (
            <>
              {!serverChecked && (
                <div className="deploy-tree-user-server-rev-id" style={{ textAlign: "center"}}>
                  {(node.isTemplateFile || node.isDocument) ? (node.userspace?.revisionId || "0") : ""}
                </div>
              )}
              <div className="deploy-tree-user-rev-id" style={{ textAlign: "center"}}>
                {node.userspace?.localRevisionId || ""}
              </div>
              <div className="deploy-tree-user-modified" style={{ textAlign: "center"}}>
                {node.userspace?.lastModified || ""}
              </div>
            </>
          )}
          {serverChecked && (
            <>
              <div className="deploy-tree-server-rev-id" style={{ textAlign: "center"}}>
                {(node.isTemplateFile || node.isDocument) ? (node.server?.revisionId || "0") : ""}
              </div>
              <div className="deploy-tree-server-user" style={{ textAlign: "center"}}>
                {node.server?.lastModifiedUser || ""}
              </div>
              <div className="deploy-tree-server-modified" style={{ textAlign: "center"}}>
                {node.server?.lastModified || ""}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const updateTreeNodeChildren = (nodeKey, children) => {
    const updateNodeRecursively = (nodes) => {
      return nodes.map((node) => {
        if (node.key === nodeKey) {
          return { ...node, children };
        }
        if (node.children && node.children.length > 0) {
          return { ...node, children: updateNodeRecursively(node.children) };
        }
        return node;
      });
    };

    setTreeData((prevTreeData) => updateNodeRecursively(prevTreeData));
  };

  const setNodeLoadingState = (nodeKey, isLoading) => {
    const updateNodeRecursively = (nodes) => {
      return nodes.map((node) => {
        if (node.key === nodeKey) {
          return { ...node, isLoading };
        }
        if (node.children && node.children.length > 0) {
          return { ...node, children: updateNodeRecursively(node.children) };
        }
        return node;
      });
    };

    setTreeData((prevTreeData) => updateNodeRecursively(prevTreeData));
  };

  const transformFileListToTreeNodes = (fileList, parentKey, parentWfGroup) => {
    return fileList.map((file, idx) => ({
      // title: file.isDocument ? file?.name : file?.nameWithoutExtension || file?.name,
      title: file?.name,
      name: file?.nameWithoutExtension,
      key: `${parentKey}-file-${idx}`,
      type: file?.templateType,
      workflowGroup: parentWfGroup,
      userspace: file?.userspace, 
      server: file?.server,
      status: file?.status,
      isDocument: file?.isDocument,
      isTemplateFile: file?.isTemplateFile,
      isLocked: file?.isLocked,
      lockStatus: file?.lockStatus,
      children: [],
    }));
  };

  const onSelect = (keys, info) => {
    const { node } = info;
    const { key } = node;
    
    // Reset context menu state on selection
    setContextMenuNode(null);
    setContextMenuType(null);
    
    // Handle multi-select with Ctrl/Cmd and Shift keys
    const isCtrlPressed = info.nativeEvent?.ctrlKey || info.nativeEvent?.metaKey;
    const isShiftPressed = info.nativeEvent?.shiftKey;
    
    let newSelectedKeys;
    if (isCtrlPressed) {
      // Ctrl+click: toggle selection
      newSelectedKeys = selectedKeys.includes(key) 
        ? selectedKeys.filter(k => k !== key)
        : [...selectedKeys, key];
    } else if (isShiftPressed && selectedKeys.length > 0) {
      // Shift+click: select range from last selected to current
      const lastSelectedKey = selectedKeys[selectedKeys.length - 1];
      const rangeKeys = getNodeRange(lastSelectedKey, key, treeData, expandedKeys);
      
      // Combine existing selection with range, removing duplicates
      const existingKeysSet = new Set(selectedKeys);
      rangeKeys.forEach(rangeKey => existingKeysSet.add(rangeKey));
      newSelectedKeys = Array.from(existingKeysSet);
    } else {
      // Normal click: single selection
      newSelectedKeys = [key];
    }
    
    setSelectedKeys(newSelectedKeys);
    // Update selected nodes based on new selected keys
    const newSelectedNodes = getSelectedNodesFromKeys(newSelectedKeys, treeData);
    setSelectedNodes(newSelectedNodes);

  };

  const onRightClick = (info) => {
    const { event, node } = info;
    event.preventDefault();
    
    // First, select the node if it's not already selected
    if (!selectedKeys.includes(node.key)) {
      setSelectedKeys([node.key]);
      setSelectedNodes([node]);
    }
    
    // Determine if this is a template or folder
    const isTemplate = node.isTemplateFile || node.isDocument;
    
    setContextMenuNode(node);
    setContextMenuType(isTemplate ? 'template' : 'folder');
  };



  const onExpand = (keys, info) => {
    setExpandedKeys(keys);
  };

  const setUserSpaceCheckedSafe = (newValue) => {
    // Ensure at least one checkbox remains checked
    if (!newValue && !serverChecked) {
      return; // Don't allow both to be unchecked
    }
    setUserSpaceChecked(newValue);
  };

  const setServerCheckedSafe = (newValue) => {
    // Ensure at least one checkbox remains checked
    if (!newValue && !userSpaceChecked) {
      return; // Don't allow both to be unchecked
    }
    setServerChecked(newValue);
  };

  const handleUserSpaceChange = (event) => {
    setUserSpaceCheckedSafe(event.target.checked);
  };

  const handleServerChange = (event) => {
    setServerCheckedSafe(event.target.checked);
  };

  const transformFolderListToTree = (folderList) => {
    return folderList.map((folder, folderIdx) => ({
      title: folder.name,
      key: `dp-${folderIdx}`,
      type: folder.type,
      children: (folder.templateFolders || []).filter(child => (child?.files?.length || child?.fileCount || 0) > 0).map((child, childIdx) => ({
        title: child?.name,
        key: `dp-${folderIdx}-${childIdx}`,
        type: child?.type,
        fileCount: child?.files?.length || child?.fileCount,
        workflowGroup: folder?.name,
        folderPath: child?.folderPath,
        workflow_only: child.templateType === "WORKFLOW" ? "Yes" : "No",
        children: transformFileListToTreeNodes(child?.files || [], `dp-${folderIdx}-${childIdx}`, folder?.name  )
      })),
    }));
  };

  // Helper function to find the best fallback node when original selection is gone
  const findBestFallbackNode = useCallback((originalKeys, newTreeData) => {
    const allNewKeys = getAllTreeKeys(newTreeData);
    const fallbackKeys = [];
    const fallbackNodes = [];

    for (const originalKey of originalKeys) {
      // Try to find exact match first
      if (allNewKeys.includes(originalKey)) {
        fallbackKeys.push(originalKey);
        const node = getSelectedNodesFromKeys([originalKey], newTreeData)[0];
        if (node) fallbackNodes.push(node);
        continue;
      }

      // Parse the original key to understand its structure
      // Keys are in format: dp-{folderIdx}-{childIdx}-file-{fileIdx} for files
      // or dp-{folderIdx}-{childIdx} for folders
      // or dp-{folderIdx} for workflow groups
      const keyParts = originalKey.split('-');
      
      if (keyParts.length >= 2) {
        // Try to find parent folder first
        if (keyParts.length > 3) { // This was a file, try parent folder
          const parentKey = keyParts.slice(0, 3).join('-'); // dp-{folderIdx}-{childIdx}
          if (allNewKeys.includes(parentKey)) {
            fallbackKeys.push(parentKey);
            const node = getSelectedNodesFromKeys([parentKey], newTreeData)[0];
            if (node) fallbackNodes.push(node);
            continue;
          }
        }
        
        // Try to find workflow group
        if (keyParts.length > 1) {
          const workflowGroupKey = keyParts.slice(0, 2).join('-'); // dp-{folderIdx}
          if (allNewKeys.includes(workflowGroupKey)) {
            fallbackKeys.push(workflowGroupKey);
            const node = getSelectedNodesFromKeys([workflowGroupKey], newTreeData)[0];
            if (node) fallbackNodes.push(node);
            continue;
          }
        }
      }
    }

    // If no fallbacks found and we have new tree data, select first item
    if (fallbackKeys.length === 0 && newTreeData.length > 0) {
      fallbackKeys.push(newTreeData[0].key);
      fallbackNodes.push(newTreeData[0]);
    }

    return { keys: [...new Set(fallbackKeys)], nodes: fallbackNodes };
  }, []);

  // Helper function to analyze selected files and determine if they belong to same folder
  const analyzeFileSelections = useCallback((originalSelectedKeys, originalSelectedNodes) => {
    const typeMap = {
      "INBOUND": "inbound_templates",
      "OUTBOUND": "outbound_templates",
       "WORKFLOW" : ""
    }
    const fileSelections = originalSelectedKeys
      .filter(key => key.includes('-file-'))
      .map(key => {
        const node = originalSelectedNodes.find(n => n.key === key);
        const keyParts = key.split('-');
        const folderKey = keyParts.slice(0, 3).join('-'); // dp-{folderIdx}-{childIdx}
        
        return {
          fileKey: key,
          fileName: node?.title,
          folderKey: folderKey,
          workflowGroup: node?.workflowGroup,
          folderPath: typeMap[node.type] || '',
          node: node
        };
      })
      .filter(selection => selection.fileName); // Only include valid selections

    if (fileSelections.length === 0) {
      return { shouldLoadFiles: false, folderToLoad: null, filesToRestore: [] };
    }

    // Check if all files belong to same folder
    const uniqueFolders = [...new Set(fileSelections.map(s => s.folderKey))];
    
    if (uniqueFolders.length === 1) {
      // All files are from same folder - we can make single API call
      const folderKey = uniqueFolders[0];
      const firstFile = fileSelections[0];
      
      return {
        shouldLoadFiles: true,
        folderToLoad: {
          key: folderKey,
          workflowGroup: firstFile.workflowGroup,
          folderPath: firstFile.folderPath
        },
        filesToRestore: fileSelections.map(s => s.fileName)
      };
    }

    // Files are from different folders - fallback to folder selection only
    return { shouldLoadFiles: false, folderToLoad: null, filesToRestore: [] };
  }, []);

  // Helper function to load files for a specific folder and restore selections
  const loadSingleFolderFiles = useCallback((folderInfo, filesToRestore, newTreeData) => {
    // Find the folder node in the new tree
    const folderNode = getSelectedNodesFromKeys([folderInfo.key], newTreeData)[0];
    if (!folderNode || folderNode.type !== "template_folder" || folderNode.fileCount === 0) {
      return;
    }

    // Set loading state for this specific node
    setNodeLoadingState(folderNode.key, true);
    
    dispatch(
      api.endpoints.getFileList.initiate({
        account: urlParams.account,
        userspace: userSpaceChecked,
        server: serverChecked,
        wf_group_name: folderInfo.workflowGroup,
        folder_path: folderInfo.folderPath,
      }, {
        forceRefetch: true
      })
    )
    .then((result) => {
      // Remove loading state
      setNodeLoadingState(folderNode.key, false);

      if (result.data && result.data.current_state === "SUCCESS") {
        // Transform and assign to children array
        const transformedChildren = transformFileListToTreeNodes(
          result.data.data.files || [],
          folderNode.key,
          folderNode.workflowGroup,
        );
        updateTreeNodeChildren(folderNode.key, transformedChildren);

        // Find matching files by name
        const matchingFiles = transformedChildren.filter(child => 
          filesToRestore.includes(child.title)
        );

        if (matchingFiles.length > 0) {
          // Update selection to include these files instead of the folder
          setSelectedKeys(prev => {
            // Remove the folder from selection and add the matching files
            const withoutFolder = prev.filter(key => key !== folderNode.key);
            return [...withoutFolder, ...matchingFiles.map(file => file.key)];
          });
          setSelectedNodes(prev => {
            // Remove the folder from selection and add the matching files
            const withoutFolder = prev.filter(node => node.key !== folderNode.key);
            return [...withoutFolder, ...matchingFiles];
          });
        }

        // Auto-expand the folder to show children
        setExpandedKeys(prev => [...prev, folderNode.key]);
      }
    })
    .catch((error) => {
      // Remove loading state on error
      setNodeLoadingState(folderNode.key, false);
      console.error("Single folder file load failed:", error);
    });
  }, [dispatch, urlParams.account, userSpaceChecked, serverChecked]);

  const refreshTreeData = useCallback((preserveState = false) => {
    setLoadingFolderList(true);
    
    // Capture current state at the time of the call if preserveState is true
    const currentExpandedKeys = preserveState ? expandedKeysRef.current : [];
    const currentSelectedKeys = preserveState ? selectedKeysRef.current : [];
    const currentSelectedNodes = preserveState ? selectedNodesRef.current : [];
    
    dispatch(
      api.endpoints.getFolderList.initiate({
        account: urlParams.account,
        userspace: userSpaceChecked,
        server: serverChecked,
      },{forceRefetch: true})
    )
      .then((result) => {
        if (result.data.current_state === "SUCCESS") {
          setLoadingFolderList(false);
          const tree = transformFolderListToTree(
            result.data.data.workflowGroups
          );
          setTreeData(tree);
          
          if (preserveState && (currentExpandedKeys.length > 0 || currentSelectedKeys.length > 0)) {
            // Get all available keys in new tree
            const allNewKeys = getAllTreeKeys(tree);
            
            // Restore expanded keys that still exist
            const validExpandedKeys = currentExpandedKeys.filter(key => allNewKeys.includes(key));
            setExpandedKeys(validExpandedKeys);
            
            // Find best fallback for selected nodes
            if (currentSelectedKeys.length > 0) {
              const fallback = findBestFallbackNode(currentSelectedKeys, tree);
              setSelectedKeys(fallback.keys);
              setSelectedNodes(fallback.nodes);
              
              // const fileAnalysis = analyzeFileSelections(currentSelectedKeys, currentSelectedNodes);
              
              // if (fileAnalysis.shouldLoadFiles) {
              //   // All selected files belong to same folder - make single API call
              //   setTimeout(() => {
              //     loadSingleFolderFiles(fileAnalysis.folderToLoad, fileAnalysis.filesToRestore, tree);
              //   }, 100); 
              // }
            } else {
              setSelectedKeys([]);
              setSelectedNodes([]);
            }
          } else {
            // Clear selected keys and context menu state on normal refresh
            setSelectedKeys([]);
            setSelectedNodes([]);
            setExpandedKeys([]);
          }
          
          // Always clear context menu state
          setContextMenuNode(null);
          setContextMenuType(null);
        } else {
          console.error("Error:", result.error);
          setErrorAlert({ 
            open: true, 
            message: t("BOX_MSG_GENERAL_ERROR") 
          });
        }
      })
      .catch((error) => {  
        setLoadingFolderList(false);
        console.error("Request failed:", error);
        setErrorAlert({ 
          open: true, 
          message: t("BOX_MSG_GENERAL_ERROR") 
        });
      });
  }, [dispatch, urlParams.account, userSpaceChecked, serverChecked, t, findBestFallbackNode]);
  
  const openHelpPanel = () => {
    dispatch(treeActions.setShowHelpPanel(true));
  }

  const handleExport = async () => {
    try {
      const response = await dispatch(
        api.endpoints.exportDeployCSV.initiate(
          { account: urlParams.account },
          { forceRefetch: true }
        )
      );
      if (response?.data) {
        if (response.data.startsWith && response.data.startsWith("{")) {
          setErrorAlert({ open: true, message: t("BOX_MSG_GENERAL_ERROR") });
          return;
        }
        const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = `${urlParams.account}_deploy_export.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (response?.error) {
        setErrorAlert({ open: true, message: t("BOX_MSG_GENERAL_ERROR") });
      }
    } catch (error) {
      console.error("Export failed:", error);
      setErrorAlert({ open: true, message: t("BOX_MSG_GENERAL_ERROR") });
    }
  };

  const renderTemplateLegendTooltip = () => (
    <div className="deploy-legend-tooltip">
      <div className="deploy-legend-list">
        <div className="deploy-legend-item">
          <span className="SERVER_USER_DEPLOY template-help"></span>
          <Typography color="inherit">{t("TEMP_LOC_BOTH")}</Typography>
        </div>
        <Divider />
        <div className="deploy-legend-item">
          <span className="SERVER template-help"></span>
          <Typography color="inherit">{t("TEMP_LOC_SERVER")}</Typography>
        </div>
        <Divider />
        <div className="deploy-legend-item">
          <span className="USER_SPACE template-help"></span>
          <Typography color="inherit">{t("TEMP_LOC_USERSPACE")}</Typography>
        </div>
        <Divider />
        <div className="deploy-legend-item">
          <span className="LOCK_AND_USERSPACE template-help"></span>
          <Typography color="inherit">Locked on Server and in User Space</Typography>
        </div>
        <Divider />
        <div className="deploy-legend-item">
          <span className="LOCK template-help"></span>
          <Typography color="inherit">Locked on Server</Typography>
        </div>
      </div>
    </div>
  );
  
  useEffect(() => {
    refreshTreeData();
    // Fetch help documents for the help panel
    dispatch(api.endpoints.getHelpTreeList.initiate({account: urlParams.account}));
  }, [userSpaceChecked, serverChecked, urlParams.account, dispatch, refreshTreeData]); 

  return (
    <div className="deploy-page-container">
      <div className="deploy-main-container">
        <aside className={`deploy-sidebar ${sidebarOpen ? "is-open" : "is-collapsed"}`}>
          <div className="deploy-sidebar-header">
            <LightTooltip title={sidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}>
              <span>
                <IconButton
                  size="small"
                  className="deploy-sidebar-toggle"
                  onClick={() => setSidebarOpen((prev) => !prev)}
                >
                  {sidebarOpen ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
              </span>
            </LightTooltip>
          </div>

          {sidebarOpen ? (
            <>
              <div className="deploy-sidebar-section">
                <Typography variant="caption" className="deploy-sidebar-title">Filters</Typography>
                <div className="deploy-filter-checkbox-container">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userSpaceChecked}
                        onChange={handleUserSpaceChange}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">User Space</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={serverChecked}
                        onChange={handleServerChange}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Server</Typography>}
                  />
                </div>
              </div>

              <div className="deploy-sidebar-section">
                <Typography variant="caption" className="deploy-sidebar-title">Info</Typography>
                <LightTooltip
                  title={renderTemplateLegendTooltip()}
                  className="deploy-legend-tooltip-popper"
                  PopperProps={{
                    modifiers: [
                      { name: "offset", options: { offset: [6, 0] } },
                    ],
                  }}
                >
                  <Button
                    size="small"
                    variant="text"
                    className="deploy-sidebar-text-button"
                    startIcon={<InfoOutlinedIcon fontSize="small" />}
                  >
                    Info
                  </Button>
                </LightTooltip>
              </div>

              <div className="deploy-sidebar-section">
                <Typography variant="caption" className="deploy-sidebar-title">Actions</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  className="deploy-sidebar-action-button"
                  startIcon={<KeyboardDoubleArrowDownIcon fontSize="small" />}
                  onClick={handleExpandAll}
                  disabled={treeData.length === 0}
                >
                  Expand All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  className="deploy-sidebar-action-button"
                  startIcon={<KeyboardDoubleArrowUpIcon fontSize="small" />}
                  onClick={handleCollapseAll}
                  disabled={treeData.length === 0}
                >
                  Collapse All
                </Button>
              </div>

              <div className="deploy-sidebar-section deploy-sidebar-section-bottom">
                {serverChecked && (
                  <Button
                    size="small"
                    variant="outlined"
                    className="deploy-sidebar-action-button deploy-sidebar-action-button--outlined"
                    onClick={handleExport}
                    startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
                  >
                    Export
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  className="deploy-sidebar-action-button deploy-sidebar-action-button--outlined"
                  onClick={() => {
                    openHelpPanel();
                  }}
                  startIcon={<HelpOutlineIcon fontSize="small" />}
                >
                  {t("HELP")}
                </Button>
              </div>
            </>
          ) : (
            <div className="deploy-sidebar-icon-list">
              <LightTooltip title="User Space">
                <span>
                  <IconButton
                    size="small"
                    className={`deploy-sidebar-icon-button ${userSpaceChecked ? "is-active" : ""}`}
                    onClick={() => setUserSpaceCheckedSafe(!userSpaceChecked)}
                  >
                    <span className="USER_SPACE deploy-sidebar-icon-symbol"></span>
                  </IconButton>
                </span>
              </LightTooltip>
              <LightTooltip title="Server">
                <span>
                  <IconButton
                    size="small"
                    className={`deploy-sidebar-icon-button ${serverChecked ? "is-active" : ""}`}
                    onClick={() => setServerCheckedSafe(!serverChecked)}
                  >
                    <span className="SERVER deploy-sidebar-icon-symbol"></span>
                  </IconButton>
                </span>
              </LightTooltip>
              <LightTooltip
                title={renderTemplateLegendTooltip()}
                className="deploy-legend-tooltip-popper"
                PopperProps={{
                  modifiers: [
                    { name: "offset", options: { offset: [6, 0] } },
                  ],
                }}
              >
                <span>
                  <IconButton size="small" className="deploy-sidebar-icon-button">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </LightTooltip>
              <LightTooltip title="Expand All Items">
                <span>
                  <IconButton
                    size="small"
                    className="deploy-sidebar-icon-button"
                    onClick={handleExpandAll}
                    disabled={treeData.length === 0}
                  >
                    <KeyboardDoubleArrowDownIcon fontSize="small" />
                  </IconButton>
                </span>
              </LightTooltip>
              <LightTooltip title="Collapse All Items">
                <span>
                  <IconButton
                    size="small"
                    className="deploy-sidebar-icon-button"
                    onClick={handleCollapseAll}
                    disabled={treeData.length === 0}
                  >
                    <KeyboardDoubleArrowUpIcon fontSize="small" />
                  </IconButton>
                </span>
              </LightTooltip>
              {serverChecked && (
                <LightTooltip title="Export">
                  <span>
                    <IconButton
                      size="small"
                      className="deploy-sidebar-icon-button"
                      onClick={handleExport}
                    >
                      <FileDownloadOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </LightTooltip>
              )}
              <LightTooltip title={t("HELP")}>
                <span>
                  <IconButton
                    size="small"
                    className="deploy-sidebar-icon-button"
                    onClick={() => {
                      openHelpPanel();
                    }}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </LightTooltip>
            </div>
          )}
        </aside>

        {/* Main Content Area */}
        <div className="deploy-main-content">
          {/* Tree Structure */}
          <div className="deploy-tree-wrapper">
            {/* Fixed Header */}
            <div className="deploy-header">
              <div className="deploy-header-tree-node">
                <span className="deploy-header-spacer-switcher"></span> 
                <span className="deploy-header-spacer-icon"></span> 
                <div className="deploy-header-indent-placeholder"></div>

                <div className="deploy-header-file-column">
                  <div className="deploy-header-file-content">
                    File
                  </div>
                </div>
                
                <div className="deploy-header-data-columns">
                  {userSpaceChecked && (
                    <>
                      {!serverChecked && (
                        <div className="deploy-header-user-server-rev-id" style={{textAlign: "center"}}>
                          Server Rev ID
                        </div>
                      )}
                      <div className="deploy-header-user-rev-id" style={{textAlign: "center"}}>
                        User Space Rev ID
                      </div>
                      <div className="deploy-header-user-modified" style={{textAlign: "center"}}>
                        User Space Last Modified Date
                      </div>
                    </>
                  )}
                  {serverChecked && (
                    <>
                      <div className="deploy-header-server-rev-id" style={{textAlign: "center"}}>
                        Server Rev ID
                      </div>
                      <div className="deploy-header-server-user" style={{textAlign: "center"}}>
                        Server Last Modified User
                      </div>
                      <div className="deploy-header-server-modified" style={{textAlign: "center"}}>
                        Server Last Modified Date
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Scrollable Tree Container */}
            <div className="deploy-scrollable-tree" id="deploy-tree-container">
              {loadingFolderList ? (
                <div className="deploy-loading-container">
                  <CircularProgress size={30} />
                </div>
              ) : (
                <Tree
                  className="deploy-tree-custom deploy-tree"
                  treeData={treeData}
                  titleRender={titleRender}
                  expandedKeys={expandedKeys}
                  selectedKeys={selectedKeys}
                  onSelect={onSelect}
                  onRightClick={onRightClick}
                  showLine
                  autoExpandParent={false}
                  switcherIcon={switcherIcon}
                  onExpand={onExpand}
                  showIcon={true}
                  icon={Icon}
                  selectable={true}
                  multiple={true}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <DeployContextMenu
        targetId="deploy-tree-container"
        contextMenuNode={contextMenuNode}
        contextMenuType={contextMenuType}
        selectedKeys={selectedKeys}
        selectedNodes={selectedNodes}
        userSpaceChecked={userSpaceChecked}
        serverChecked={serverChecked}
        onRefreshTree={() => refreshTreeData(true)}
        urlParams={urlParams}
      />

      <Snackbar
        open={errorAlert.open}
        autoHideDuration={6000}
        onClose={() => setErrorAlert({ open: false, message: "" })}
      >
        <Alert
          onClose={() => setErrorAlert({ open: false, message: "" })}
          severity="error"
          variant="filled"
          sx={{ width: '100%', maxWidth: '450px', whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>

      <HelpPanel />
    </div>
  );
}

export default Deploy;
